import React, { useRef, useState } from 'react';
import { Box, Typography, Button, CircularProgress, Stack } from '@mui/material';

const CODE_LENGTH = 6;
const emptyCode = () => Array(CODE_LENGTH).fill('');

// Code-entry body shared by the two places a discount-approval OTP cycle can
// be entered: inline in DiscountDrawer.js's "Awaiting Approval" view, and
// inside DiscountOtpModal.js's Dialog (the fallback path, resumed from
// ApprovalBanner.js). No Dialog chrome of its own — the caller supplies that
// (or, for the drawer, no chrome at all — the drawer body already is the
// focused context).
const DiscountOtpCodeEntry = ({ cycle, secondsLeft, resendCooldown, isResending, isVerifying, onVerify, onResend }) => {
  const [digits, setDigits] = useState(emptyCode());
  const [error, setError] = useState('');
  const inputRefs = useRef([]);

  const handleDigitChange = (index, value) => {
    const v = value.replace(/[^0-9]/g, '').slice(0, 1);
    setDigits((prev) => {
      const next = [...prev];
      next[index] = v;
      return next;
    });
    if (error) setError('');
    if (v && index < CODE_LENGTH - 1) inputRefs.current[index + 1]?.focus();
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !digits[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handleVerify = async () => {
    const code = digits.join('');
    if (code.length < CODE_LENGTH) return;
    const result = await onVerify(code);
    if (!result?.ok) {
      setError(result?.error || 'Incorrect verification code');
      setDigits(emptyCode());
      inputRefs.current[0]?.focus();
    }
  };

  const clampedSeconds = Math.max(secondsLeft, 0);
  const minutes = Math.floor(clampedSeconds / 60);
  const seconds = clampedSeconds % 60;
  const timerLow = clampedSeconds <= 30;
  // One recipient row per verified phone number, so the same approver can
  // appear multiple times (e.g. their primary number + additional OTP
  // numbers) — dedupe for display, the cashier only needs who to ask, not
  // how many of that person's phones got a code.
  const recipientNames = [ ...new Set((cycle?.recipients || []).map((r) => r.name)) ];

  return (
    <Box>
      <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1} sx={{ mb: 1.5 }}>
        <Typography fontSize="0.72rem" color="#64748B" sx={{ minWidth: 0 }}>
          Texted to{' '}
          {recipientNames.map((name, i) => (
            <React.Fragment key={name}>
              <Typography component="span" fontWeight={700} color="#1E293B" fontSize="0.72rem">{name}</Typography>
              {i < recipientNames.length - 1 ? ', ' : ''}
            </React.Fragment>
          ))}
        </Typography>
        <Box sx={{ flexShrink: 0, px: 1, py: 0.25, borderRadius: '999px', bgcolor: '#EEF2FF' }}>
          <Typography fontSize="0.62rem" fontWeight={800} color="#4F46E5" letterSpacing="0.02em">
            CYCLE {cycle?.cycleNumber} OF 3
          </Typography>
        </Box>
      </Stack>

      <Stack direction="row" spacing={{ xs: 0.5, sm: 0.75 }} justifyContent="center" sx={{ mb: 1.25 }}>
        {digits.map((d, i) => (
          <Box
            key={i}
            component="input"
            inputMode="numeric"
            maxLength={1}
            value={d}
            ref={(el) => { inputRefs.current[i] = el; }}
            onChange={(e) => handleDigitChange(i, e.target.value)}
            onKeyDown={(e) => handleKeyDown(i, e)}
            sx={{
              width: { xs: 28, sm: 34 }, height: { xs: 36, sm: 40 },
              textAlign: 'center', fontSize: '1.05rem', fontWeight: 700,
              border: '1.5px solid', borderColor: error ? '#EF4444' : '#E2E8F0',
              borderRadius: '8px', color: '#1E293B', fontFamily: 'inherit',
              '&:focus': { outline: 'none', borderColor: '#4F46E5', boxShadow: '0 0 0 3px #EEF2FF' },
            }}
          />
        ))}
      </Stack>

      {error && (
        <Typography fontSize="0.68rem" fontWeight={600} color="#DC2626" sx={{ mb: 1 }}>
          ✕ {error}
        </Typography>
      )}

      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.25 }}>
        <Typography fontSize="0.68rem" fontWeight={timerLow ? 700 : 400} color={timerLow ? '#DC2626' : '#94A3B8'}>
          Code expires in {minutes}:{String(seconds).padStart(2, '0')}
        </Typography>
        <Button
          size="small"
          onClick={onResend}
          disabled={resendCooldown > 0 || isResending}
          sx={{ fontSize: '0.68rem', fontWeight: 700, textTransform: 'none', color: '#4F46E5', p: 0, minWidth: 0, '&.Mui-disabled': { color: '#CBD5E1' } }}
        >
          {isResending ? <CircularProgress size={12} /> : resendCooldown > 0 ? `Resend in ${resendCooldown}s` : 'Resend code'}
        </Button>
      </Stack>

      <Button
        fullWidth
        variant="contained"
        disableElevation
        onClick={handleVerify}
        disabled={isVerifying}
        sx={{ textTransform: 'none', fontWeight: 800, borderRadius: '8px', bgcolor: '#4F46E5', boxShadow: 'none', '&:hover': { bgcolor: '#4338CA' } }}
      >
        {isVerifying ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : 'Verify code'}
      </Button>
    </Box>
  );
};

export default DiscountOtpCodeEntry;
